import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Store, ShoppingCart, Phone, Car, Building2, Wrench, RectangleHorizontal, Check, Loader2, User, Building, Save, FileText, Edit3, Plus, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import DashboardLayout from '../components/DashboardLayout';
import { TranslateButton } from '../components/TranslateButton';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// أيقونات المتاجر
const STORE_ICONS = {
  'ShoppingCart': ShoppingCart,
  'Phone': Phone,
  'RectangleHorizontal': RectangleHorizontal,
  'Car': Car,
  'Building2': Building2,
  'Wrench': Wrench,
  'Store': Store,
};

// قائمة الأيقونات المتاحة للاختيار
const AVAILABLE_ICONS = [
  { id: 'Store', icon: Store, name_ar: 'متجر', name_en: 'Store' },
  { id: 'ShoppingCart', icon: ShoppingCart, name_ar: 'سلة', name_en: 'Cart' },
  { id: 'Phone', icon: Phone, name_ar: 'هاتف', name_en: 'Phone' },
  { id: 'Car', icon: Car, name_ar: 'سيارة', name_en: 'Car' },
  { id: 'Building2', icon: Building2, name_ar: 'مبنى', name_en: 'Building' },
  { id: 'Wrench', icon: Wrench, name_ar: 'أدوات', name_en: 'Tools' },
  { id: 'RectangleHorizontal', icon: RectangleHorizontal, name_ar: 'لوحة', name_en: 'Plate' },
];

// الألوان المتاحة
const AVAILABLE_COLORS = [
  '#c9a227', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#f97316', '#14b8a6', '#ef4444'
];

export default function StoreTypesManagement() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { language } = useLanguage();
  
  const [storeTypes, setStoreTypes] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [editingDescription, setEditingDescription] = useState(null);
  
  // Modal state for adding new type
  const [showAddModal, setShowAddModal] = useState(false);
  const [newType, setNewType] = useState({
    id: '',
    name_ar: '',
    name_en: '',
    description_ar: '',
    description_en: '',
    icon: 'Store',
    color: '#c9a227',
    activity_types: ['personal', 'company'],
    is_active: true
  });
  const [addingType, setAddingType] = useState(false);
  // Auto-translate state for AR ↔ EN fields
  const [translatingField, setTranslatingField] = useState(null);
  
  const headers = { Authorization: `Bearer ${token}` };

  // Auto-translate a single field using /api/translate (Gemini).
  // Fires on blur when one side is filled and the other is empty.
  const autoTranslate = async ({ text, direction, fieldKey, applyFn }) => {
    if (!text || !text.trim()) return;
    setTranslatingField(fieldKey);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/translate`,
        { text: text.trim(), direction },
        { headers }
      );
      const translated = (res.data?.translated || '').trim();
      if (translated) applyFn(translated);
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل في الترجمة التلقائية' : 'Auto-translate failed');
    } finally {
      setTranslatingField(null);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchStoreTypes();
  }, []);

  useEffect(() => {
    if (originalData.length > 0 && storeTypes.length > 0) {
      const changed = JSON.stringify(storeTypes) !== JSON.stringify(originalData);
      setHasChanges(changed);
    }
  }, [storeTypes, originalData]);

  const fetchStoreTypes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/store-types`, { headers });
      const data = res.data.data || [];
      setStoreTypes(JSON.parse(JSON.stringify(data)));
      setOriginalData(JSON.parse(JSON.stringify(data)));
    } catch (err) {
      toast.error('فشل في جلب أنواع المتاجر');
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = (typeId) => {
    setStoreTypes(prev => prev.map(st => 
      st.id === typeId ? { ...st, is_active: !st.is_active } : st
    ));
  };

  const toggleActivityType = (typeId, activityType) => {
    setStoreTypes(prev => prev.map(st => {
      if (st.id !== typeId) return st;
      
      const currentTypes = st.activity_types || [];
      let newTypes;
      
      if (currentTypes.includes(activityType)) {
        newTypes = currentTypes.filter(t => t !== activityType);
      } else {
        newTypes = [...currentTypes, activityType];
      }
      
      if (newTypes.length === 0) {
        toast.error('يجب اختيار نوع نشاط واحد على الأقل');
        return st;
      }
      
      return { ...st, activity_types: newTypes };
    }));
  };

  const updateDescription = (typeId, field, value) => {
    setStoreTypes(prev => prev.map(st => 
      st.id === typeId ? { ...st, [field]: value } : st
    ));
  };

  const saveAllChanges = async () => {
    setSaving(true);
    try {
      const changes = storeTypes.filter((st) => {
        const original = originalData.find(o => o.id === st.id);
        return JSON.stringify(st) !== JSON.stringify(original);
      });

      for (const st of changes) {
        await axios.put(`${BACKEND_URL}/api/store-types/${st.id}`, {
          is_active: st.is_active,
          activity_types: st.activity_types,
          description_ar: st.description_ar || '',
          description_en: st.description_en || ''
        }, { headers });
      }

      toast.success(`تم حفظ ${changes.length} تغيير بنجاح`);
      setOriginalData(JSON.parse(JSON.stringify(storeTypes)));
      setHasChanges(false);
      setEditingDescription(null);
    } catch (err) {
      toast.error('فشل في حفظ التغييرات');
    } finally {
      setSaving(false);
    }
  };

  const cancelChanges = () => {
    setStoreTypes(JSON.parse(JSON.stringify(originalData)));
    setHasChanges(false);
    setEditingDescription(null);
  };

  // إضافة نوع متجر جديد
  const handleAddNewType = async () => {
    if (!newType.id.trim() || !newType.name_ar.trim() || !newType.name_en.trim()) {
      toast.error(language === 'ar' ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill all required fields');
      return;
    }

    // التحقق من عدم وجود ID مكرر
    if (storeTypes.some(st => st.id === newType.id)) {
      toast.error(language === 'ar' ? 'هذا المعرف موجود مسبقاً' : 'This ID already exists');
      return;
    }

    setAddingType(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/store-types`, newType, { headers });
      toast.success(language === 'ar' ? 'تم إضافة نوع المتجر بنجاح' : 'Store type added successfully');
      setShowAddModal(false);
      setNewType({
        id: '',
        name_ar: '',
        name_en: '',
        description_ar: '',
        description_en: '',
        icon: 'Store',
        color: '#c9a227',
        activity_types: ['personal', 'company'],
        is_active: true
      });
      fetchStoreTypes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل في إضافة نوع المتجر');
    } finally {
      setAddingType(false);
    }
  };

  return (
    <DashboardLayout
      title="التحكم بأنواع المتاجر"
      titleEn="Store Types Management"
      subtitle="إدارة وتفعيل أنواع المتاجر المتاحة"
      subtitleEn="Manage and enable available store types"
      screenCode="BIZ-C03"
      backPath="/control/cloudgm-business"
      onBack={() => navigate('/control/cloudgm-business')}
      breadcrumb={[
        { labelAr: 'التحكم والمعلومات للمنصات', labelEn: 'Platform Control & Info', path: '/dashboard?view=platform-control' },
        { labelAr: 'CLOUDGM FZ LLC (للتحكم)', labelEn: 'CLOUDGM FZ LLC (Control)', path: '/dashboard?view=platform-control&sub=fzlc' },
        { labelAr: 'التحكم والمعلومات (منصة التجار)', labelEn: 'Control & Info (Merchants)', path: '/control/cloudgm-business' },
        { labelAr: 'التحكم بأنواع المتاجر', labelEn: 'Store Types Management' },
      ]}
    >
      {/* Main Content */}
      <main className="relative z-10 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Action Bar */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-between mb-8"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-purple-500/10 flex items-center justify-center border border-purple-500/20">
                <Store className="h-7 w-7 text-purple-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  {language === 'ar' ? 'أنواع المتاجر' : 'Store Types'}
                </h2>
                <p className="text-sm text-[#71717A]">
                  {storeTypes.length} {language === 'ar' ? 'نوع متجر' : 'store types'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {hasChanges && (
                <>
                  <Button
                    onClick={cancelChanges}
                    variant="outline"
                    className="border-white/10 text-white hover:bg-white/5"
                  >
                    {language === 'ar' ? 'إلغاء' : 'Cancel'}
                  </Button>
                  <Button
                    onClick={saveAllChanges}
                    disabled={saving}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {language === 'ar' ? 'حفظ التغييرات' : 'Save Changes'}
                  </Button>
                </>
              )}
              
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#d4af37] text-black font-bold rounded-xl hover:shadow-[0_0_30px_rgba(212,175,55,0.3)] transition-all"
              >
                <Plus className="h-5 w-5" />
                {language === 'ar' ? 'إضافة نوع جديد' : 'Add New Type'}
              </motion.button>
            </div>
          </motion.div>

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-10 w-10 animate-spin text-[#d4af37]" />
            </div>
          ) : (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden"
            >
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-4 p-4 bg-white/[0.02] border-b border-white/10 text-sm font-semibold text-[#71717A]">
                <div className="col-span-3 flex items-center gap-2">
                  <Store className="h-4 w-4" />
                  {language === 'ar' ? 'نوع المتجر' : 'Store Type'}
                </div>
                <div className="col-span-3 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  {language === 'ar' ? 'الوصف' : 'Description'}
                </div>
                <div className="col-span-3 flex items-center justify-center gap-2">
                  <User className="h-4 w-4" />
                  {language === 'ar' ? 'نوع النشاط' : 'Activity Type'}
                </div>
                <div className="col-span-1 flex items-center justify-center">
                  {language === 'ar' ? 'النشطة' : 'Active'}
                </div>
                <div className="col-span-2 flex items-center justify-center">
                  {language === 'ar' ? 'الحالة' : 'Status'}
                </div>
              </div>

              {/* Table Body */}
              <div className="divide-y divide-white/5">
                {storeTypes.map((storeType, index) => {
                  const IconComponent = STORE_ICONS[storeType.icon] || Store;
                  const original = originalData.find(o => o.id === storeType.id);
                  const isChanged = JSON.stringify(storeType) !== JSON.stringify(original);
                  const isEditing = editingDescription === storeType.id;
                  
                  return (
                    <motion.div 
                      key={storeType.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={`grid grid-cols-12 gap-4 p-4 items-center transition-all ${
                        isChanged ? 'bg-[#d4af37]/5' : 'hover:bg-white/[0.02]'
                      }`}
                    >
                      {/* نوع المتجر */}
                      <div className="col-span-3 flex items-center gap-3">
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: `${storeType.color}15` }}
                        >
                          <IconComponent className="h-6 w-6" style={{ color: storeType.color }} />
                        </div>
                        <div>
                          <p className="font-medium text-white flex items-center gap-2">
                            {language === 'ar' ? storeType.name_ar : storeType.name_en}
                            {isChanged && <span className="w-2 h-2 rounded-full bg-[#d4af37]"></span>}
                          </p>
                          <p className="text-xs text-[#71717A]">{storeType.id}</p>
                        </div>
                      </div>

                      {/* الوصف */}
                      <div className="col-span-3">
                        {isEditing ? (
                          <div className="space-y-2">
                            <Input
                              value={storeType.description_ar || ''}
                              onChange={(e) => updateDescription(storeType.id, 'description_ar', e.target.value)}
                              placeholder="الوصف بالعربية"
                              className="bg-white/5 border-white/10 text-white text-sm"
                            />
                            <Input
                              value={storeType.description_en || ''}
                              onChange={(e) => updateDescription(storeType.id, 'description_en', e.target.value)}
                              placeholder="Description in English"
                              className="bg-white/5 border-white/10 text-white text-sm"
                              dir="ltr"
                            />
                            <button
                              onClick={() => setEditingDescription(null)}
                              className="text-xs text-[#d4af37] hover:underline"
                            >
                              {language === 'ar' ? 'إغلاق' : 'Close'}
                            </button>
                          </div>
                        ) : (
                          <div 
                            onClick={() => setEditingDescription(storeType.id)}
                            className="cursor-pointer group"
                          >
                            {(storeType.description_ar || storeType.description_en) ? (
                              <div className="space-y-1">
                                {storeType.description_ar && (
                                  <p className="text-sm text-white truncate max-w-[200px]">{storeType.description_ar}</p>
                                )}
                                {storeType.description_en && (
                                  <p className="text-xs text-[#71717A] truncate max-w-[200px]" dir="ltr">{storeType.description_en}</p>
                                )}
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 text-[#71717A] group-hover:text-[#d4af37] transition-colors">
                                <Edit3 className="h-4 w-4" />
                                <span className="text-sm">{language === 'ar' ? 'إضافة وصف' : 'Add description'}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* نوع النشاط */}
                      <div className="col-span-3 flex items-center justify-center gap-2">
                        <button
                          onClick={() => toggleActivityType(storeType.id, 'personal')}
                          className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border transition-all text-xs ${
                            (storeType.activity_types || []).includes('personal')
                              ? 'bg-sky-500/20 border-sky-500/50 text-sky-400'
                              : 'bg-white/5 border-white/10 text-[#71717A] hover:border-white/20'
                          }`}
                        >
                          <User className="h-3.5 w-3.5" />
                          <span>{language === 'ar' ? 'شخصي' : 'Personal'}</span>
                        </button>

                        <button
                          onClick={() => toggleActivityType(storeType.id, 'company')}
                          className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border transition-all text-xs ${
                            (storeType.activity_types || []).includes('company')
                              ? 'bg-purple-500/20 border-purple-500/50 text-purple-400'
                              : 'bg-white/5 border-white/10 text-[#71717A] hover:border-white/20'
                          }`}
                        >
                          <Building className="h-3.5 w-3.5" />
                          <span>{language === 'ar' ? 'شركة' : 'Company'}</span>
                        </button>
                      </div>

                      {/* عدد المتاجر */}
                      <div className="col-span-1 flex items-center justify-center">
                        <span className="px-3 py-1.5 bg-white/5 rounded-xl text-sm text-white font-medium">
                          {storeType.active_stores_count || 0}
                        </span>
                      </div>

                      {/* حالة التفعيل */}
                      <div className="col-span-2 flex items-center justify-center">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => toggleActive(storeType.id)}
                          className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all border ${
                            storeType.is_active
                              ? 'bg-emerald-500/20 border-emerald-500/50'
                              : 'bg-white/5 border-white/10 hover:border-white/20'
                          }`}
                        >
                          <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${
                            storeType.is_active
                              ? 'border-emerald-400 bg-emerald-500'
                              : 'border-[#71717A]'
                          }`}>
                            {storeType.is_active && <Check className="h-4 w-4 text-white" />}
                          </div>
                        </motion.button>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Info Card */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-6 bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10"
          >
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#d4af37]" />
              {language === 'ar' ? 'ملاحظات هامة' : 'Important Notes'}
            </h3>
            <ul className="space-y-3 text-sm text-[#A1A1AA]">
              <li className="flex items-start gap-3">
                <Check className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                <span>{language === 'ar' ? 'تفعيل نوع المتجر يجعله متاحاً للتجار عند إنشاء متجر جديد' : 'Activating a store type makes it available for merchants when creating a new store'}</span>
              </li>
              <li className="flex items-start gap-3">
                <Plus className="h-4 w-4 text-[#d4af37] mt-0.5 flex-shrink-0" />
                <span>{language === 'ar' ? 'يمكنك إضافة أنواع متاجر جديدة عبر زر "إضافة نوع جديد"' : 'You can add new store types via the "Add New Type" button'}</span>
              </li>
              <li className="flex items-start gap-3">
                <User className="h-4 w-4 text-sky-400 mt-0.5 flex-shrink-0" />
                <span>{language === 'ar' ? 'نوع النشاط "شخصي" للأفراد الذين يبيعون منتجاتهم الخاصة' : '"Personal" activity type is for individuals selling their own products'}</span>
              </li>
              <li className="flex items-start gap-3">
                <Building className="h-4 w-4 text-purple-400 mt-0.5 flex-shrink-0" />
                <span>{language === 'ar' ? 'نوع النشاط "شركة" للشركات والمؤسسات التجارية' : '"Company" activity type is for businesses and commercial entities'}</span>
              </li>
            </ul>
          </motion.div>
        </div>
      </main>

      {/* Add New Type Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Plus className="h-5 w-5 text-[#d4af37]" />
              {language === 'ar' ? 'إضافة نوع متجر جديد' : 'Add New Store Type'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* ID */}
            <div>
              <label className="text-sm text-[#A1A1AA] mb-2 block">{language === 'ar' ? 'المعرف (ID) *' : 'ID *'}</label>
              <Input
                value={newType.id}
                onChange={(e) => setNewType(prev => ({ ...prev, id: e.target.value.toLowerCase().replace(/\s/g, '_') }))}
                placeholder="ecommerce"
                className="bg-white/5 border-white/10 text-white"
                dir="ltr"
              />
              <p className="text-xs text-[#71717A] mt-1">{language === 'ar' ? 'معرف فريد بدون مسافات' : 'Unique ID without spaces'}</p>
            </div>

            {/* Names */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-[#A1A1AA] mb-2 flex items-center justify-between">
                  <span>{language === 'ar' ? 'الاسم بالعربية *' : 'Arabic Name *'}</span>
                  {translatingField === 'ar' && (
                    <span className="flex items-center gap-1 text-[10px] text-cyan-400">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      {language === 'ar' ? 'جارٍ الترجمة...' : 'Translating...'}
                    </span>
                  )}
                </label>
                <div className="flex gap-2 items-start">
                  <Input
                    value={newType.name_ar}
                    onChange={(e) => setNewType(prev => ({ ...prev, name_ar: e.target.value.replace(/[A-Za-z]+/g, '') }))}
                    onBlur={() => {
                      if (newType.name_ar.trim() && !newType.name_en.trim()) {
                        autoTranslate({
                          text: newType.name_ar,
                          direction: 'ar_to_en',
                          fieldKey: 'en',
                          applyFn: (t) => setNewType(p => ({ ...p, name_en: t })),
                        });
                      }
                    }}
                    placeholder="التجارة الإلكترونية"
                    dir="rtl"
                    className="bg-white/5 border-white/10 text-white flex-1"
                    data-testid="new-storetype-ar"
                  />
                  <TranslateButton
                    sourceText={newType.name_ar}
                    direction="ar_to_en"
                    onTranslated={(t) => setNewType(p => ({ ...p, name_en: t }))}
                    testId="storetype-translate-ar-to-en"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-[#A1A1AA] mb-2 flex items-center justify-between">
                  <span>{language === 'ar' ? 'الاسم بالإنجليزية *' : 'English Name *'}</span>
                  {translatingField === 'en' && (
                    <span className="flex items-center gap-1 text-[10px] text-cyan-400">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      {language === 'ar' ? 'جارٍ الترجمة...' : 'Translating...'}
                    </span>
                  )}
                </label>
                <div className="flex gap-2 items-start">
                  <Input
                    value={newType.name_en}
                    onChange={(e) => setNewType(prev => ({ ...prev, name_en: e.target.value.replace(/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]+/g, '') }))}
                    onBlur={() => {
                      if (newType.name_en.trim() && !newType.name_ar.trim()) {
                        autoTranslate({
                          text: newType.name_en,
                          direction: 'en_to_ar',
                          fieldKey: 'ar',
                          applyFn: (t) => setNewType(p => ({ ...p, name_ar: t })),
                        });
                      }
                    }}
                    placeholder="E-Commerce"
                    className="bg-white/5 border-white/10 text-white flex-1"
                    dir="ltr"
                    data-testid="new-storetype-en"
                  />
                  <TranslateButton
                    sourceText={newType.name_en}
                    direction="en_to_ar"
                    onTranslated={(t) => setNewType(p => ({ ...p, name_ar: t }))}
                    testId="storetype-translate-en-to-ar"
                  />
                </div>
              </div>
            </div>
            <p className="text-xs text-[#606060] -mt-2">
              {language === 'ar'
                ? '💡 كلا الاسمين العربي والإنجليزي إجباريّان — اكتب أحدهما ثم انقر خارج الحقل لترجمته تلقائياً.'
                : '💡 Both Arabic and English names are required — type one then click outside the field for auto-translation.'}
            </p>

            {/* Icon Selection */}
            <div>
              <label className="text-sm text-[#A1A1AA] mb-2 block">{language === 'ar' ? 'الأيقونة' : 'Icon'}</label>
              <div className="flex flex-wrap gap-2">
                {AVAILABLE_ICONS.map((item) => {
                  const IconComp = item.icon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => setNewType(prev => ({ ...prev, icon: item.id }))}
                      className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all border ${
                        newType.icon === item.id
                          ? 'bg-[#d4af37]/20 border-[#d4af37]'
                          : 'bg-white/5 border-white/10 hover:border-white/20'
                      }`}
                    >
                      <IconComp className="h-5 w-5" style={{ color: newType.icon === item.id ? '#d4af37' : '#A1A1AA' }} />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Color Selection */}
            <div>
              <label className="text-sm text-[#A1A1AA] mb-2 block">{language === 'ar' ? 'اللون' : 'Color'}</label>
              <div className="flex flex-wrap gap-2">
                {AVAILABLE_COLORS.map((color) => (
                  <button
                    key={color}
                    onClick={() => setNewType(prev => ({ ...prev, color }))}
                    className={`w-10 h-10 rounded-xl transition-all border-2 ${
                      newType.color === color ? 'border-white scale-110' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>

            {/* Activity Types */}
            <div>
              <label className="text-sm text-[#A1A1AA] mb-2 block">{language === 'ar' ? 'أنواع النشاط' : 'Activity Types'}</label>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    const types = newType.activity_types;
                    if (types.includes('personal')) {
                      if (types.length > 1) setNewType(prev => ({ ...prev, activity_types: types.filter(t => t !== 'personal') }));
                    } else {
                      setNewType(prev => ({ ...prev, activity_types: [...types, 'personal'] }));
                    }
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-all ${
                    newType.activity_types.includes('personal')
                      ? 'bg-sky-500/20 border-sky-500/50 text-sky-400'
                      : 'bg-white/5 border-white/10 text-[#71717A]'
                  }`}
                >
                  <User className="h-4 w-4" />
                  {language === 'ar' ? 'شخصي' : 'Personal'}
                </button>
                <button
                  onClick={() => {
                    const types = newType.activity_types;
                    if (types.includes('company')) {
                      if (types.length > 1) setNewType(prev => ({ ...prev, activity_types: types.filter(t => t !== 'company') }));
                    } else {
                      setNewType(prev => ({ ...prev, activity_types: [...types, 'company'] }));
                    }
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-all ${
                    newType.activity_types.includes('company')
                      ? 'bg-purple-500/20 border-purple-500/50 text-purple-400'
                      : 'bg-white/5 border-white/10 text-[#71717A]'
                  }`}
                >
                  <Building className="h-4 w-4" />
                  {language === 'ar' ? 'شركة' : 'Company'}
                </button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAddModal(false)}
              className="border-white/10 text-white hover:bg-white/5"
            >
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={handleAddNewType}
              disabled={
                addingType ||
                !newType.id.trim() ||
                !newType.name_ar.trim() ||
                !newType.name_en.trim()
              }
              className="bg-[#d4af37] hover:bg-[#c9a227] text-black disabled:opacity-40"
              data-testid="storetype-submit-btn"
            >
              {addingType ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {language === 'ar' ? 'إضافة' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
